import React, { useContext, useState } from 'react';
import { notification, Spin, Input, Button, Flex, Form, Image } from 'antd';
import { productTypeAsString } from '@leedpulse-monorepo/enums';
import classNames from 'classnames';
import { useTheme } from '../../contexts/ui/ThemeContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';

import { LeedflowLogoLight as logoLeedflow } from '@leedpulse-monorepo/assets';

// Same look as the reset page, the user just left a mail with a temporary password
import styles from '../password-reset-page/PasswordResetForm.module.css';

interface PasswordChangeFormValues {
  currentPassword?: string;
  password: string;
  confirmPassword: string;
}

const PASSWORD_MIN_LENGTH = 8;

const PasswordChangeForm: React.FC = () => {
  const { themeName } = useTheme();
  const authContext = useContext(AuthenticationContext);
  const [form] = Form.useForm<PasswordChangeFormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [needsCurrentPassword, setNeedsCurrentPassword] = useState(false);

  const handleSubmit = async (values: PasswordChangeFormValues) => {
    setIsLoading(true);

    const result = await authContext.changeRequiredPassword({
      newPassword: values.password,
      currentPassword: needsCurrentPassword ? values.currentPassword : undefined,
    });

    setIsLoading(false);

    if (result.currentPasswordRequired) {
      setNeedsCurrentPassword(true);
      notification.info({
        message: 'Confirmation requise',
        description: 'Pour des raisons de sécurité, saisissez aussi le mot de passe reçu par mail.',
      });
    } else if (result.success) {
      notification.success({ message: 'Mot de passe mis à jour' });
    } else {
      notification.error({ message: 'Erreur', description: result.error });
    }
  };

  return (
    <div className={styles.resetPassword}>
      {/* Decorative arcs - Top Right */}
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr1)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr2)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr3)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcTr4)} />

      {/* Decorative arcs - Bottom Left */}
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl1)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl2)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl3)} />
      <div className={classNames(styles.resetPasswordArc, styles.resetPasswordArcBl4)} />

      <div className={styles.resetPasswordContainer}>
        <div className={styles.resetPasswordLogo}>
          <Image
            src={logoLeedflow}
            alt="Logo"
            preview={false}
            style={{
              marginTop: "1rem",
              marginBottom: "1.5rem",
              padding: "0.75rem 1.5rem",
            }}
          />
        </div>

        <p className={styles.resetPasswordSubtitle}>
          Le mot de passe reçu par mail est temporaire. Choisissez le vôtre pour accéder à votre espace.
        </p>

        <Form
          className={styles.resetPasswordForm}
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {needsCurrentPassword && (
            <div className={styles.resetPasswordField}>
              <div className={styles.resetPasswordInputWrapper}>
                <Form.Item
                  label="Mot de passe reçu par mail :"
                  name="currentPassword"
                  rules={[{ required: true, message: 'Le mot de passe reçu par mail est obligatoire.' }]}
                >
                  <Input.Password className={classNames(styles.resetPasswordInput, styles.resetPasswordInputPassword)} />
                </Form.Item>
              </div>
            </div>
          )}

          <div className={styles.resetPasswordField}>
            <div className={styles.resetPasswordInputWrapper}>
              <Form.Item
                label="Nouveau mot de passe :"
                name="password"
                rules={[
                  { required: true, message: 'Le mot de passe est obligatoire.' },
                  { min: PASSWORD_MIN_LENGTH, message: `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères.` },
                ]}
              >
                <Input.Password className={classNames(styles.resetPasswordInput, styles.resetPasswordInputPassword)} />
              </Form.Item>
            </div>
          </div>

          <div className={styles.resetPasswordField}>
            <div className={styles.resetPasswordInputWrapper}>
              <Form.Item
                label="Confirmation du mot de passe :"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'La confirmation du mot de passe est obligatoire.' },
                  ({ getFieldValue }) => ({
                    validator: (_, value) => {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Les mots de passe ne correspondent pas.'));
                    },
                  }),
                ]}
              >
                <Input.Password className={classNames(styles.resetPasswordInput, styles.resetPasswordInputPassword)} />
              </Form.Item>
            </div>
          </div>

          <Flex vertical gap={8}>
            <Button
              type="primary"
              htmlType="submit"
              style={{ width: '100%' }}
              disabled={isLoading}
            >
              {isLoading ? <Spin size="small" /> : 'Valider mon mot de passe'}
            </Button>
            <Button
              type="link"
              onClick={() => authContext.submitSignOut()}
              disabled={isLoading}
            >
              Se déconnecter
            </Button>
          </Flex>
        </Form>

        <footer className={styles.resetPasswordFooter}>
          <p className={styles.resetPasswordCopyright}>{productTypeAsString(themeName)} • Tous droits réservés</p>
        </footer>
      </div>
    </div>
  );
};

export default PasswordChangeForm;